import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { errorText, Icon, PopItem, Popover, usePopover, useToast } from '../ui/kit';
import type { IconName } from '../ui/icons';
import { AUTH_MODE, canApproveExpenses, isEmployee, ROLE_NAMES, useSession, useTenant, type Role } from '../auth/session';
import { saveThemeToAccount } from '../lib/schemeSync';
import { applyTheme, useTheme } from '../lib/theme';
import { useTenantDetails } from '../api/hooks';
import { fmtDate } from '../lib/format';
import { AddBusinessModal } from './AddBusiness';
import { readPref, writePref } from '../lib/prefs';
import { SearchPalette } from './SearchPalette';
import { NotificationsButton } from './Notifications';
import { Logo } from '../ui/Logo';
import { APP_NAME } from '../lib/brand';
import { useBankList } from '../pages/dashboard/data';
import { businessColor, companiesLabel, initial, linesToReview } from './companies';
import { ClientFinder } from '../pages/practice/ClientFinder';
import { rememberPractice } from '../pages/practice/data';
import { SwitcherPopover } from './Switcher';
import { useAccountantOpenCount, useHasAccountant } from '../pages/accountant/data';
import '../pages/practice/practice.css';
import { HelpContext, type HelpView } from '../lib/help';
import { HelpPanel } from './help/HelpPanel';
import { useSupportRequests, useWhatsNew } from './help/data';

const LOGO = <Logo />;

type NavEntry = { to: string; icon: IconName; label: string } | { group: string; icon: IconName; label: string; items: [string, string][] };
// Only screens the API supports today. The other prototype screens come back as their endpoints
// are built.
const NAV: NavEntry[] = [
  { to: '/', icon: 'dashboard', label: 'Dashboard' },
  { group: 'sales', icon: 'doc', label: 'Sales', items: [['/quotes', 'Quotes'], ['/invoices', 'Invoices'], ['/recurring-invoices', 'Recurring invoices'], ['/credit-notes', 'Credit notes'], ['/payments-received', 'Payments received'], ['/customers', 'Customers'], ['/items', 'Items']] },
  { group: 'purchases', icon: 'bill', label: 'Purchases', items: [['/purchase-orders', 'Purchase orders'], ['/bills', 'Bills'], ['/supplier-credits', 'Supplier credits'], ['/payments-made', 'Payments made'], ['/suppliers', 'Suppliers']] },
  { group: 'expenses', icon: 'card', label: 'Expenses', items: [['/expenses', 'My expenses'], ['/expenses/review', 'To review'], ['/expenses/reimburse', 'Reimburse'], ['/expenses/rates', 'Travel rates']] },
  { group: 'banking', icon: 'bank', label: 'Banking', items: [['/banking', 'Accounts and transactions'], ['/reconcile', 'Reconciliations']] },
  { group: 'accounting', icon: 'scale', label: 'Accounting', items: [['/accounts', 'Chart of accounts'], ['/journals', 'Journal entries'], ['/fixed-assets', 'Fixed assets'], ['/reports/general-ledger', 'General ledger']] },
  { to: '/reports', icon: 'chart', label: 'Reports' },
  // The business's own papers (ADR 0031). Not in an employee's menu: they never see the vault.
  { to: '/vault', icon: 'folder', label: 'Document vault' },
];
/** An employee's menu: only their own expenses (ADR 0023). Time tracking and payroll join it later. */
const EMPLOYEE_NAV: NavEntry[] = [{ to: '/expenses', icon: 'card', label: 'My expenses' }];
/** Approving and reimbursing are for owners, admins and accountants. */
const APPROVER_ONLY = ['/expenses/review', '/expenses/reimburse', '/expenses/rates'];
export function navFor(role: Role): NavEntry[] {
  if (isEmployee(role)) return EMPLOYEE_NAV;
  if (canApproveExpenses(role)) return NAV;
  return NAV.map((n) => ('items' in n ? { ...n, items: n.items.filter(([to]) => !APPROVER_ONLY.includes(to)) } : n));
}
/** The menu with From your accountant after the dashboard. */
const withAccountant = (nav: NavEntry[]): NavEntry[] => [nav[0]!, { to: '/from-your-accountant', icon: 'mail', label: 'From your accountant' }, ...nav.slice(1)];
/** The search shortcut as this keyboard shows it. */
const SHORTCUT = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

/** Which nav entry a path belongs to, so detail pages keep their section highlighted. */
function sectionOf(path: string): string {
  if (path.startsWith('/expenses/review')) return '/expenses/review';
  if (path.startsWith('/expenses/reimburse')) return '/expenses/reimburse';
  if (path.startsWith('/expenses')) return '/expenses';
  if (path.startsWith('/expenses/rates')) return '/expenses/rates';
  if (path.startsWith('/invoices')) return '/invoices';
  if (path.startsWith('/recurring-invoices')) return '/recurring-invoices';
  if (path.startsWith('/quotes')) return '/quotes';
  if (path.startsWith('/purchase-orders')) return '/purchase-orders';
  if (path.startsWith('/bills')) return '/bills';
  if (path.startsWith('/credit-notes')) return '/credit-notes';
  if (path.startsWith('/supplier-credits')) return '/supplier-credits';
  if (path.startsWith('/payments-received') || path.startsWith('/customer-refunds')) return '/payments-received';
  if (path.startsWith('/payments-made') || path.startsWith('/supplier-refunds')) return '/payments-made';
  if (path.startsWith('/customers')) return '/customers';
  if (path.startsWith('/suppliers')) return '/suppliers';
  if (path.startsWith('/reports/general-ledger')) return '/reports/general-ledger';
  if (path.startsWith('/reports')) return '/reports';
  if (path.startsWith('/reconcile')) return '/reconcile';
  if (path.startsWith('/journals')) return '/journals';
  if (path.startsWith('/fixed-assets')) return '/fixed-assets';
  if (path.startsWith('/banking')) return '/banking';
  if (path.startsWith('/vault')) return '/vault';
  return path;
}

export function Shell() {
  const session = useSession();
  const tenant = useTenant();
  const details = useTenantDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(readPref('mybooks.menuCollapsed') === '1');
  const [drawer, setDrawer] = useState(false);
  const [groups, setGroups] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [searching, setSearching] = useState(false);
  // Client books mode (PRACTICE-DESIGN.md 4): a practice member working in a client's company.
  const client = session.client;
  const clientPractice = client ? session.practices.find((p) => p.id === client.practiceId) ?? null : null;
  const [switchingClient, setSwitchingClient] = useState(false);
  const tenantPop = usePopover(), createPop = usePopover(), profilePop = usePopover(), groupPop = usePopover();
  // The help panel (APP-DESIGN.md section 10). Its dot: a What's new entry not seen yet, or support
  // answered a request.
  const [help, setHelp] = useState<HelpView | null>(null);
  const openHelp = useCallback((view: HelpView = { kind: 'home' }) => { setDrawer(false); setSearching(false); setHelp(view); }, []);
  const whatsNew = useWhatsNew();
  const supportUpdates = useSupportRequests('all', 2).data?.updates ?? 0;
  const helpDot = !!whatsNew.data?.unseen || supportUpdates > 0;
  // The group whose pages the collapsed menu shows beside its icon.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const key = sectionOf(location.pathname);
  // Bank lines to review, as an amber count on Banking. Employees have no Banking.
  const bank = useBankList(!isEmployee(tenant.role));
  const toReview = linesToReview(bank.data ?? []);
  const lockedThrough = (details.data as { locked_through?: string | null } | undefined)?.locked_through;
  const employee = isEmployee(tenant.role);
  // From your accountant, with its open count, once a practice keeps the books (PRACTICE-DESIGN.md 7).
  const hasAccountant = useHasAccountant();
  const fromAccountant = useAccountantOpenCount();
  const accountantCount = fromAccountant.questions + fromAccountant.documents;
  const nav = hasAccountant ? withAccountant(navFor(tenant.role)) : navFor(tenant.role);
  const userInitials = (session.user?.name ?? session.user?.email ?? 'U').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

  useEffect(() => { setDrawer(false); document.getElementById('view')?.focus({ preventScroll: true }); window.scrollTo(0, 0); }, [location.pathname]);
  // Cmd+K / Ctrl+K opens search from anywhere, as in the prototype. Employees have no search.
  useEffect(() => {
    if (employee) return;
    // In a client's books, ⌘K switches client; the menu's search still searches these books.
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setDrawer(false); if (clientPractice) setSwitchingClient(true); else setSearching(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [employee, clientPractice]);

  const switchTo = (id: string, name: string) => {
    session.selectTenant(id);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== id });
    navigate('/');
    toast(`Switched to ${name}`);
  };
  const toggleCollapsed = () => { setCollapsed((c) => { writePref('mybooks.menuCollapsed', c ? '0' : '1'); return !c; }); };
  const toPractice = (id: string) => { rememberPractice(id); navigate('/practice'); };

  return (
    <HelpContext.Provider value={openHelp}>
    {client && (
      <div className="client-bar" role="region" aria-label="Client books">
        <span className="client-bar-text">You're in <b>{client.name}</b> · {client.practiceName}{tenant.role === 'read_only' ? ' · Read-only' : ''}</span>
        <span className="client-bar-actions">
          <button type="button" className="client-bar-btn" onClick={() => { rememberPractice(client.practiceId); navigate(`/practice/clients/${client.linkId}`); }}>Back to client page</button>
          {clientPractice && <button type="button" className="client-bar-btn" onClick={() => setSwitchingClient(true)}>Switch client <kbd>{SHORTCUT}</kbd></button>}
        </span>
      </div>
    )}
    <div className={`app ${collapsed ? 'collapsed' : ''} ${drawer ? 'drawer-open' : ''} ${client ? 'with-client-bar' : ''}`}>
      <nav className="rail" aria-label="Shortcuts">
        <NavLink className="rail-brand" to="/" aria-label={`${APP_NAME} home`}>{LOGO}</NavLink>
        <button className="rail-btn menu-btn" onClick={() => setDrawer(true)} aria-label="Open the menu"><Icon name="dashboard" size={20} /></button>
        {employee ? (
          <NavLink className="rail-btn" to="/expenses/new" data-tip="New expense" aria-label="New expense"><Icon name="plus" size={20} /></NavLink>
        ) : (
          <>
            <button className="rail-btn" onClick={() => setSearching(true)} data-tip={`Search  ${SHORTCUT}`} aria-label="Search" aria-haspopup="dialog"><Icon name="search" size={20} /></button>
            <button className="rail-btn" onClick={createPop.toggle} data-tip="Create" aria-label="Create" aria-haspopup="true" aria-expanded="false"><Icon name="plus" size={20} /></button>
            <NotificationsButton />
            <NavLink className={({ isActive }) => `rail-btn ${isActive ? 'active' : ''}`} to="/settings" data-tip="Settings" aria-label="Settings"><Icon name="gear" size={20} /></NavLink>
          </>
        )}
        <button className="rail-btn" onClick={() => openHelp()} data-tip="Help" aria-label={helpDot ? 'Help, something new' : 'Help'} aria-haspopup="dialog"><Icon name="help" size={20} />{helpDot && <span className="mini-dot rail-dot" aria-hidden="true" />}</button>
        <ThemeSwitch />
        <button className="rail-avatar" onClick={profilePop.toggle} aria-label="Your profile" aria-haspopup="true" aria-expanded="false">{userInitials}</button>
      </nav>
      <aside className="sidebar" aria-label="Menu">
        <div className="side-head">
          <button className="tenant-switch" onClick={tenantPop.toggle} aria-haspopup="true" aria-expanded="false" aria-label="Switch company">
            <div className="tenant-avatar" style={{ background: businessColor(tenant.id) }}>{initial(tenant.name)}</div>
            <div style={{ minWidth: 0 }}><div className="tenant-name">{details.data?.name ?? tenant.name}</div><div className="tenant-role">{client ? `Client of ${client.practiceName}` : companiesLabel(session.tenants.length)}</div></div>
            <svg className="updown" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m7 15 5 5 5-5M7 9l5-5 5 5" /></svg>
          </button>
          <button className="collapse-btn" onClick={toggleCollapsed} aria-label="Hide the menu" title="Hide the menu"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
        </div>
        {!employee && <button type="button" className="search search-btn side-search" onClick={() => { setDrawer(false); setSearching(true); }} aria-haspopup="dialog"><Icon name="search" /><span style={{ flex: 1 }}>Search…</span><kbd>{SHORTCUT}</kbd></button>}
        <nav className="nav" aria-label="Main navigation">
          {nav.map((n) => {
            if (!('items' in n)) return <NavLink key={n.to} end className={`nav-item ${key === n.to ? 'active' : ''}`} to={n.to} aria-current={key === n.to ? 'page' : undefined}><Icon name={n.icon} size={18} />{n.label}
              {n.to === '/from-your-accountant' && accountantCount > 0 && <span className="nav-badge" title={`${accountantCount} waiting for you`}>{accountantCount}<span className="visually-hidden"> waiting</span></span>}</NavLink>;
            // Groups start closed and open only when clicked.
            const open = groups[n.group] === true;
            const hasActive = n.items.some(([to]) => to === key);
            return (
              <div className="nav-group" key={n.group}>
                <button className={`group-btn ${hasActive ? 'has-active' : ''}`} aria-expanded={open} onClick={() => setGroups((g) => ({ ...g, [n.group]: !open }))}><Icon name={n.icon} size={18} />{n.label}
                  {n.group === 'banking' && toReview > 0 && <span className="nav-badge" title={`${toReview} bank ${toReview === 1 ? 'line' : 'lines'} to review`}>{toReview}<span className="visually-hidden"> to review</span></span>}</button>
                {open && <div className="nav-sub">{n.items.map(([to, label]) => <NavLink key={to} className={`sub-item ${to === key ? 'active' : ''}`} to={to} aria-current={to === key ? 'page' : undefined}>{label}</NavLink>)}</div>}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">{lockedThrough ? <><Icon name="lock" size={13} />Books locked through {fmtDate(lockedThrough)}</> : null}</div>
        {/* Collapsed on a wide screen, the menu keeps its icons; a group's pages open beside it. */}
        <div className="side-mini">
          <button className="mini-tenant" onClick={tenantPop.toggle} aria-haspopup="true" aria-expanded="false" aria-label={`Switch company (${details.data?.name ?? tenant.name})`} data-tip={details.data?.name ?? tenant.name}>
            <span className="tenant-avatar" style={{ background: businessColor(tenant.id) }}>{initial(tenant.name)}</span>
          </button>
          <button className="expand-btn" onClick={toggleCollapsed} aria-label="Show the menu" title="Show the menu"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></button>
          <nav className="mini-nav" aria-label="Main navigation, collapsed">
            {nav.map((n) => {
              if (!('items' in n)) return <NavLink key={n.to} end className={`mini-item ${key === n.to ? 'active' : ''}`} to={n.to} aria-label={n.label} data-tip={n.label} aria-current={key === n.to ? 'page' : undefined}><Icon name={n.icon} size={18} /></NavLink>;
              const hasActive = n.items.some(([to]) => to === key);
              return (
                <button key={n.group} type="button" className={`mini-item ${hasActive ? 'active' : ''}`} aria-label={n.label} data-tip={openGroup === n.group && groupPop.anchor ? undefined : n.label} aria-haspopup="true" aria-expanded="false"
                  onClick={(e) => { setOpenGroup(n.group); groupPop.toggle(e); }}><Icon name={n.icon} size={18} />{n.group === 'banking' && toReview > 0 && <span className="mini-dot" aria-hidden="true" />}</button>
              );
            })}
          </nav>
          {lockedThrough && <span className="mini-lock" title={`Books locked through ${fmtDate(lockedThrough)}`}><Icon name="lock" size={14} /></span>}
        </div>
      </aside>
      <div className="drawer-scrim" onClick={() => setDrawer(false)} hidden={!drawer} />
      <div className="main">
        <main className="content" id="view" tabIndex={-1}><div className="page"><Outlet /></div></main>
      </div>

      {tenantPop.anchor && (
        <SwitcherPopover anchor={tenantPop.anchor} onClose={tenantPop.close} currentCompany={client ? undefined : tenant.id}
          onPractice={(id) => toPractice(id)} onCompany={(id, name) => switchTo(id, name)}
          extra={<>
            <div className="pop-sep" />
            {!employee && !client && <PopItem icon="chart" title="Group financial reporting" sub="Single company, combined or consolidated" to="/reports/group" />}
            <PopItem icon="plus" title="Add a company" sub="Its own books, team and settings" onClick={() => { tenantPop.close(); setAdding(true); }} />
            {session.archived.length > 0 && <div className="pop-sep" />}
            {session.archived.map((a) => (
              <PopItem key={a.id} icon="back" title={`Restore ${a.name}`} sub="Archived. Everything in it is as you left it"
                onClick={() => { tenantPop.close(); session.restoreBusiness(a.id).then(() => { navigate('/'); toast(`${a.name} is back`); }, (e) => toast(errorText(e))); }} />
            ))}
          </>} />
      )}
      {createPop.anchor && (
        <Popover anchor={createPop.anchor} onClose={createPop.close} label="Create">
          <div className="pop-title">Create</div>
          <PopItem icon="doc" title="Quote" sub="Offer a price before you invoice" to="/quotes/new" />
          <PopItem icon="doc" title="Invoice" sub="Bill a customer" to="/invoices/new" />
          <PopItem icon="repeat" title="Recurring invoice" sub="Bill a customer on a schedule" to="/recurring-invoices/new" />
          <PopItem icon="bill" title="Bill" sub="Record what you owe a supplier" to="/bills/new" />
          <PopItem icon="box" title="Purchase order" sub="Order goods or services from a supplier" to="/purchase-orders/new" />
          <PopItem icon="doc" title="Credit note" sub="Give a customer credit" to="/credit-notes/new" />
          <PopItem icon="bill" title="Supplier credit" sub="Record credit a supplier gave you" to="/supplier-credits/new" />
          <div className="pop-sep" />
          <PopItem icon="cash" title="Receive payment" sub="Against one or more invoices" to="/payments-received?new=1" />
          <PopItem icon="cash" title="Pay bills" sub="Pay one or more bills of a supplier" to="/payments-made?new=1" />
          <div className="pop-sep" />
          <PopItem icon="people" title="Customer" to="/customers?new=1" />
          <PopItem icon="truck" title="Supplier" to="/suppliers?new=1" />
          <PopItem icon="box" title="Item" sub="A product or service you sell" to="/items?new=1" />
          <PopItem icon="scale" title="Journal entry" sub="Adjust the books directly" to="/journals/new" />
          <PopItem icon="card" title="Expense" sub="Claim a receipt you paid" to="/expenses/new" />
          <div className="pop-sep" />
          <PopItem icon="upload" title="Import a bank statement" sub="CSV, OFX, QFX or OFC" to="/banking?import=1" />
        </Popover>
      )}
      {groupPop.anchor && openGroup && (() => {
        const group = nav.find((n) => 'items' in n && n.group === openGroup);
        if (!group || !('items' in group)) return null;
        return (
          <Popover anchor={groupPop.anchor} onClose={groupPop.close} label={group.label}>
            <div className="pop-title">{group.label}</div>
            {group.items.map(([to, label]) => <PopItem key={to} title={label} to={to} checked={to === key} extra={to === key ? <Icon name="check" /> : undefined} />)}
          </Popover>
        );
      })()}
      {profilePop.anchor && (
        <Popover anchor={profilePop.anchor} onClose={profilePop.close} label="Your profile">
          <div className="pop-item static"><span className="user-chip">{userInitials}</span><span><span className="pi-title" style={{ display: 'block' }}>{session.user?.name ?? session.user?.email}</span><span className="pi-sub" style={{ display: 'block' }}>{session.user?.email} · {ROLE_NAMES[tenant.role]}</span></span></div>
          <div className="pop-sep" />
          {!employee && <><PopItem icon="gear" title="Company settings" sub="Team, details and books" to="/settings" /><PopItem icon="globe" title="Global settings" sub="Billing and exchange rates" to="/account" /><div className="pop-sep" /></>}
          <PopItem icon="back" title="Sign out" onClick={() => { void session.signOut(); }} />
        </Popover>
      )}
      {searching && !employee && <SearchPalette onClose={() => setSearching(false)} onHelp={openHelp} />}
      {help && <HelpPanel view={help} onView={setHelp} onClose={() => setHelp(null)} />}
      {adding && <AddBusinessModal onClose={() => setAdding(false)} onCreated={(name) => { setAdding(false); queryClient.clear(); navigate('/'); toast(`${name} is ready. You are the owner.`); }} />}
      {switchingClient && clientPractice && <ClientFinder practice={clientPractice} books onClose={() => setSwitchingClient(false)} />}
    </div>
    </HelpContext.Provider>
  );
}

export function Placeholder({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="card empty"><b>{title}</b> {children}</div>;
}

/**
 * Light or dark mode, from the bottom of the rail. One click switches what is on screen; the choice
 * is saved in this browser and, signed in, to the account. Settings → App layout can go back to
 * following the device.
 */
export function ThemeSwitch() {
  const { effective } = useTheme();
  const toast = useToast();
  const next = effective === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? 'Dark mode' : 'Light mode';
  return (
    <button type="button" className="rail-btn rail-theme" data-tip={label} aria-label={`Switch to ${label.toLowerCase()}`}
      onClick={() => {
        applyTheme(next);
        if (AUTH_MODE === 'supabase') void saveThemeToAccount(next);
        toast(`${label} on`);
      }}>
      <Icon name={next === 'dark' ? 'moon' : 'sun'} size={20} />
    </button>
  );
}
